import { useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    Divider,
    IconButton,
    List,
    ListItem,
    Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { format, parseISO } from 'date-fns';
import { Identifier } from 'react-admin';
import type { CardComment, Card } from '@/types';
import type { CrmDataProvider } from '@/providers/types';
import { CardAttachmentList } from './CardAttachmentList';
import { FileDropzone } from '@/components/misc/FileDropzone';
import { MAX_FILE_SIZE, MAX_FILES, CARD_ACCEPT } from './cardAttachmentRules';

interface CardCommentsProps {
    card: Card;
    canUpdate: boolean;
    onChanged?: () => void;
}

export const CardComments: React.FC<CardCommentsProps> = ({
    card,
    canUpdate,
    onChanged,
}) => {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [text, setText] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<Identifier | null>(null);
    const [editText, setEditText] = useState('');
    const [editingComment, setEditingComment] = useState<CardComment | null>(null);

    const isCreator = (comment: CardComment) => comment.created_by === card.created_by;

    const handleSubmit = async () => {
        if (!text.trim() && files.length === 0) {
            notify('Write a comment or attach a file', { type: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            await dataProvider.createCardComment(card.id, text, files);
            setText('');
            setFiles([]);
            notify('Comment added', { type: 'success' });
            onChanged?.();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to create comment';
            notify(message, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async (commentId: Identifier) => {
        if (!editText.trim()) {
            notify('Comment cannot be empty', { type: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            await dataProvider.updateCardComment(card.id, commentId, editText);
            setEditingId(null);
            setEditText('');
            setEditingComment(null);
            notify('Comment updated', { type: 'success' });
            onChanged?.();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update comment';
            notify(message, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (commentId: Identifier) => {
        if (!window.confirm('Delete this comment?')) return;

        setSubmitting(true);
        try {
            await dataProvider.deleteCardComment(card.id, commentId);
            notify('Comment deleted', { type: 'success' });
            onChanged?.();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete comment';
            notify(message, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const comments = card.comments || [];

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Comments ({comments.length})
            </Typography>

            <List sx={{ mb: 3 }}>
                {comments.map(comment => (
                    <Box key={comment.id}>
                        <ListItem
                            sx={{
                                display: 'block',
                                px: 0,
                                py: 2,
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                sx={{ mb: 1 }}
                            >
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {comment.created_by_name || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {format(parseISO(comment.created_at), 'PPp')}
                                        {comment.updated_at > comment.created_at && ' (edited)'}
                                    </Typography>
                                </Box>

                                {canUpdate && isCreator(comment) && editingId !== comment.id && (
                                    <Stack direction="row">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setEditingId(comment.id);
                                                setEditText(comment.text);
                                                setEditingComment(comment);
                                            }}
                                            disabled={submitting}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(comment.id)}
                                            disabled={submitting}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                )}
                            </Stack>

                            {editingId === comment.id ? (
                                <Stack spacing={1}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={editText}
                                        onChange={e => setEditText(e.target.value)}
                                        disabled={submitting}
                                    />
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleEditSubmit(comment.id)}
                                            disabled={submitting}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                setEditingId(null);
                                                setEditText('');
                                                setEditingComment(null);
                                            }}
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        color: 'text.primary',
                                        mb: 1,
                                    }}
                                >
                                    {comment.text}
                                </Typography>
                            )}

                            {comment.attachments && comment.attachments.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <CardAttachmentList
                                        cardId={card.id}
                                        attachments={comment.attachments}
                                        canRemove={canUpdate && isCreator(comment)}
                                        onChanged={onChanged}
                                    />
                                </Box>
                            )}
                        </ListItem>
                        <Divider />
                    </Box>
                ))}
            </List>

            {canUpdate && (
                <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Add a comment
                    </Typography>

                    <Stack spacing={1.5}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Write your comment..."
                            value={text}
                            onChange={e => setText(e.target.value)}
                            disabled={submitting}
                        />

                        <FileDropzone
                            value={files}
                            onChange={setFiles}
                            maxFiles={MAX_FILES}
                            maxSize={MAX_FILE_SIZE}
                            accept={CARD_ACCEPT}
                            disabled={submitting}
                            label="Drop files here, or click to attach"
                            showHint
                        />

                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || (!text.trim() && files.length === 0)}
                        >
                            Send
                        </Button>
                    </Stack>
                </Paper>
            )}
        </Box>
    );
};

CardComments.displayName = 'CardComments';
